import { FieldViewModel, getDistinctValues, ICrop, ICropping, INMaxCropGroup, INMaxCropping, selectMany } from "../../../omnia"
import { getNMaxAdjustments } from "../../../services/nmax"
import { getSnsIndexFromAmount } from "../../../services/sns"
import { ICroppingGroupItem, INtrReq, INutritionService, IRequirementsProvider, NutrientGroup } from "../../../types"
import { CroppingReqVm } from "../../cropping-req-vm"
import { PlanVm } from "./planVm"

/// <reference path="../../cropping-req-vm.ts" />

export class PlanCroppingVm extends CroppingReqVm implements ICroppingGroupItem {


    static createFromCropping(svc: INutritionService, cropping: FieldViewModel[], plans: PlanVm[]): PlanCroppingVm[] {

        const nmax = svc.getNMaxCropGroups();
        const req = svc.getRequirementsProvider()

        return selectMany(cropping.map(item => {

            const vm = new PlanCroppingVm(String(item.croppingId), svc, item, req)

            vm.setup(plans, nmax);

            var zones = vm.getZonesPlanCroppingVm();

            return [vm].concat(zones);

        }));

    }

    public plans: PlanVm[];
    public currentPlan: PlanVm;

    public requirementsLeft: INtrReq;

    public selected: boolean;
    public expanded: boolean;

    public nutrientsRatio: any;

    public nmaxAdjustments = getNMaxAdjustments()
    public nmaxGroup: INMaxCropGroup;
    public nmaxCropping: INMaxCropping;
    public nmax: number;

    public isZone: boolean;

    public planCroppingVm: PlanCroppingVm;

    constructor(
        public readonly key: string,
        private svc: INutritionService,
        public croppingVm: FieldViewModel,
        private readonly provider: IRequirementsProvider,
        public readonly croppingId = croppingVm.croppingId) {

        super(null);

    }

    get zoneNumber(): number {
        return this.isZone && this.requirements ? this.requirements.ZoneNumber : null;
    }

    private setup(plans: PlanVm[], nmax: INMaxCropGroup[]) {

        const { provider } = this

        let croppingId = this.croppingId;

        this.plans = plans.filter(p => p.cropping && !!p.cropping.find(c => c.croppingId === croppingId));

        this.setRequirements(provider.getCroppingInitialRequirements(croppingId))
        this.requirementsLeft = provider.getCroppingClosingRequirements(croppingId);

        if (this.requirements) {
            this.nmax = this.requirements.NMax;
        }

        if (nmax) {

            let group = nmax.filter(g => !!g.Cropping).map(g => {
                return {
                    group: g,
                    cropping: g.Cropping.find(c => c.CroppingId === croppingId)
                }
            }).filter(r => !!r.cropping);

            if (group.length) {

                let r = group[0];

                this.nmaxGroup = r.group;
                this.nmaxCropping = r.cropping;

            }

        }

    }

    private setupZone(planCroppingVm: PlanCroppingVm, plans: PlanVm[], requirements: INtrReq, requirementsLeft: INtrReq) {

        let croppingId = this.croppingId;

        this.planCroppingVm = planCroppingVm;
        this.isZone = true;
        this.plans = plans.filter(p => p.cropping && !!p.cropping.find(c => c.croppingId === croppingId));

        this.setRequirements(requirements)
        this.requirementsLeft = requirementsLeft;

        if (this.requirements) {
            this.nmax = this.requirements.NMax;
        }

        if (planCroppingVm.nmaxGroup) {
            this.nmaxGroup = planCroppingVm.nmaxGroup;
            this.nmaxCropping = planCroppingVm.nmaxCropping;
        }

    }

    public getZonesPlanCroppingVm(): PlanCroppingVm[] {

        const { provider: req } = this

        var croppingReq = req.getRequirements(this.croppingId).filter(r => !r.PlanId);

        // Don't display zone if only 2 req and one is Zone=0 and the other is Zone=1 & NutrientGroup=All
        if (croppingReq.length === 2) {
            var first = croppingReq.find(r => !r.Zone);
            var second = croppingReq.find(r => r.Zone && r.NutrientGroup === NutrientGroup.AllNutrients);
            if (!!first && !!second) {
                return [];
            }
        }

        var zoneRequirements = req.getInitialRequirements(this.croppingId).filter(r =>
            r.Zone && (r.Geometry || r.NutrientGroup === NutrientGroup.AllNutrients));
        var zoneRequirementsLeft = req.getClosingRequirements(this.croppingId).filter(r =>
            r.Zone && (r.Geometry || r.NutrientGroup === NutrientGroup.AllNutrients));

        var zoneNumbers = getDistinctValues(zoneRequirements, r => r.ZoneNumber).sort((a, b) => a > b ? 1 : a < b ? -1 : 0);

        var zones = [];

        zoneNumbers.forEach(z => {

            const key = this.key + ":" + String(z)

            const vm = new PlanCroppingVm(key, this.svc, this.croppingVm, req);

            vm.setupZone(this, this.plans,
                zoneRequirements.find(r => r.ZoneNumber === z),
                zoneRequirementsLeft.find(r => r.ZoneNumber === z)
            );

            zones.push(vm);

        });

        return zones;
    }

    getCropColor() {
        return this.croppingVm.cssColor
    }

    get description(): string {
        return this.getDescription();
    }

    getDescription(includeCrop: boolean = true): string {
        return PlanCroppingVm.getCroppingDescription(this.croppingVm, includeCrop);
    }

    get field() {
        return this.croppingVm.field;
    }

    get cropId(): number {
        return this.croppingVm.cropping.CropId;
    }

    get crop(): ICrop {
        return this.croppingVm.cropping.Crop;
    }

    get cropping(): ICropping {
        return this.croppingVm.cropping;
    }

    get uncropped(): boolean {
        return this.croppingVm.uncropped;
    }

    get hasRequirements(): boolean {
        return !!this.requirements;
    }

    get isInPlan(): boolean {
        return this.plans && !!this.plans.length;
    }

    get authorized() {
        return this.croppingVm.authorized
    }

    get precision(): boolean {
        return this.requirements && this.requirements.Aggregate;
    }

    get area() {
        return this.croppingVm.area
    }

    static getCroppingDescription(croppingVm: FieldViewModel, includeCrop: boolean = true): string {

        if (includeCrop) {
            return croppingVm.getDescription();
        }

        return croppingVm.field.Name + " (" + croppingVm.cropping.Area + " ha)";

    }

    getRequirement(nutrient: string, left: boolean, planId: number): number {

        var requirement = null;

        if (planId) {

            const provider = this.provider

            if (left) {
                requirement = provider.getPlanClosingRequirements(this.croppingId, planId)
                    .find(r => this.isZone ? r.ZoneNumber === this.zoneNumber : !r.Zone);
            } else {
                requirement = provider.getPlanRequirements(this.croppingId, planId)
                    .find(r => this.isZone ? r.ZoneNumber === this.zoneNumber : !r.Zone);
            }

        } else if (left) {
            requirement = this.requirementsLeft;
        } else {
            requirement = this.requirements;
        }

        if (requirement) {
            return <number>requirement["Required" + nutrient];
        }

    }

    getSnsIndex(): number {

        if (this.requirements) {
            return getSnsIndexFromAmount(this.requirements.Sns);
        }

    }

    containsPlan(planId: number): boolean {
        return this.plans && this.plans.filter(p => p.plan.NutritionPlanId === planId).length > 0;
    }

}
